/**
 * ChatMarkdown — the single markdown renderer used across every chat /
 * session visualisation in Stream.
 *
 * Composes the global `.markdown` styling (defined in
 * `src/styles/global.css` §Markdown) with the chat-bubble helper
 * `.chat-md` (strips the trailing margin on the last child so a bubble
 * doesn't end with whitespace) and an optional `.chat-md--compact`
 * modifier for denser surfaces.
 *
 * Surfaces consuming this component:
 *   - features/session (real-time agent chat)
 *   - features/claude-sessions (via MessageText)
 *   - features/agents/playground (developer transcript)
 *   - features/inbox-manager (customer message threads)
 *   - features/sessions (audit transcript)
 *
 * If you need bespoke rendering — slash-command popups, document viewers
 * (SKILL.md), or approval renderers — continue to use ReactMarkdown
 * directly. This component is intentionally opinionated for
 * conversational text.
 */

import type { CSSProperties } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

export interface ChatMarkdownProps {
  /** Markdown source text. */
  text: string
  /**
   * Visual density preset.
   * - `comfortable` (default): the chat reading scale (14px) used in
   *   real-time sessions and the ticket logger.
   * - `compact`: denser fonts/spacing (13px) for inspector side-panes,
   *   the agents playground, and inbox-manager threads.
   */
  density?: 'comfortable' | 'compact'
  /** Extra class names appended to the wrapper. */
  className?: string
  /** Inline style for size/color overrides on the wrapper. */
  style?: CSSProperties
  /** Test id. Defaults to `'chat-markdown'`. */
  'data-testid'?: string
}

export function ChatMarkdown({
  text,
  density = 'comfortable',
  className,
  style,
  'data-testid': testId = 'chat-markdown',
}: ChatMarkdownProps) {
  const classes = [
    'markdown',
    'chat-md',
    density === 'compact' ? 'chat-md--compact' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classes} style={style} data-testid={testId}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </div>
  )
}

export default ChatMarkdown
